import { createContext, useContext, useReducer, useCallback, useMemo, ReactNode } from 'react';
import { imageReducer, initialImageState, type ImageState, type ImageAction } from './imageReducer';

interface ImageContextType {
  state: ImageState;
  dispatch: (action: ImageAction) => void;
  // Convenience methods
  selectImage: (index: number, selectedImageMessageId: string) => void;
  deselectImage: () => void;
  hoverImage: (index: number, selectedImageMessageId: string) => void;
  stopHover: () => void;
  toggleEditMode: () => void;
  setSidebarOpen: (isOpen: boolean) => void;
  resetAll: () => void;
  // Inpainting methods
  setInpaintingActive: (isActive: boolean) => void;
  setEraserMode: (isEraserMode: boolean) => void;
  setBrushSize: (brushSize: number) => void;
  setMaskData: (maskData: string) => void;
  clearInpainting: () => void;
  // Local-upload inpainting methods
  openLocalInpainting: () => void;
  setLocalUploadImage: (url: string) => void;
}

const ImageContext = createContext<ImageContextType | undefined>(undefined);

interface ImageProviderProps {
  readonly children: ReactNode;
}

export function ImageProvider({ children }: ImageProviderProps) {
  const [state, dispatch] = useReducer(imageReducer, initialImageState);

  // Convenience methods to avoid importing action types everywhere

  const selectImage = useCallback((index: number, messageId: string) => {
    dispatch({ type: 'SELECT_IMAGE', payload: { index, messageId } });
  }, []);

  const deselectImage = useCallback(() => {
    dispatch({ type: 'DESELECT_IMAGE' });
  }, []);


  const hoverImage = useCallback((index: number, hoverImageMessageId: string) => {
    dispatch({ type: 'HOVER_IMAGE', payload: { index, hoverImageMessageId } });
  }, []);

  const stopHover = useCallback(() => {
    dispatch({ type: 'STOP_HOVER' });
  }, []);

  const toggleEditMode = useCallback(() => {
    dispatch({ type: 'TOGGLE_EDIT_MODE' });
  }, []);

  const setSidebarOpen = useCallback((isOpen: boolean) => {
    dispatch({ type: 'SET_SIDEBAR_OPEN', payload: { isOpen } });
  }, []);

  const resetAll = useCallback(() => {
    dispatch({ type: 'RESET_ALL' });
  }, []);

  // Inpainting methods
  const setInpaintingActive = useCallback((isActive: boolean) => {
    dispatch({ type: 'SET_INPAINTING_ACTIVE', payload: { isActive } });
  }, []);

  const setEraserMode = useCallback((isEraserMode: boolean) => {
    dispatch({ type: 'SET_ERASER_MODE', payload: { isEraserMode } });
  }, []);

  const setBrushSize = useCallback((brushSize: number) => {
    dispatch({ type: 'SET_BRUSH_SIZE', payload: { brushSize } });
  }, []);

  const setMaskData = useCallback((maskData: string) => {
    dispatch({ type: 'SET_MASK_DATA', payload: { maskData } });
  }, []);

  const clearInpainting = useCallback(() => {
    dispatch({ type: 'CLEAR_INPAINTING' });
  }, []);

  const openLocalInpainting = useCallback(() => {
    dispatch({ type: 'OPEN_LOCAL_INPAINTING' });
  }, []);

  const setLocalUploadImage = useCallback((url: string) => {
    dispatch({ type: 'SET_LOCAL_UPLOAD_IMAGE', payload: { url } });
  }, []);

  const value: ImageContextType = useMemo(() => ({
    state,
    dispatch,
    selectImage,
    deselectImage,
    hoverImage,
    stopHover,
    toggleEditMode,
    setSidebarOpen,
    resetAll,
    setInpaintingActive,
    setEraserMode,
    setBrushSize,
    setMaskData,
    clearInpainting,
    openLocalInpainting,
    setLocalUploadImage,
  }), [
    state,
    selectImage,
    deselectImage,
    hoverImage,
    stopHover,
    toggleEditMode,
    setSidebarOpen,
    resetAll,
    setInpaintingActive,
    setEraserMode,
    setBrushSize,
    setMaskData,
    clearInpainting,
    openLocalInpainting,
    setLocalUploadImage,
  ]);

  return (
    <ImageContext.Provider value={value}>
      {children}
    </ImageContext.Provider>
  );
}

export function useImageContext(): ImageContextType {
  const context = useContext(ImageContext);
  if (context === undefined) {
    throw new Error('useImageContext must be used within an ImageProvider');
  }
  return context;
}

// Custom hooks for specific use cases
export function useImageState() {
  const { state } = useImageContext();
  return state;
}

export function useImageActions() {
  const { selectImage, deselectImage, hoverImage, stopHover, toggleEditMode, setSidebarOpen, resetAll, setInpaintingActive, setEraserMode, setBrushSize, setMaskData, clearInpainting, openLocalInpainting, setLocalUploadImage } = useImageContext();
  return { selectImage, deselectImage, hoverImage, stopHover, toggleEditMode, setSidebarOpen, resetAll, setInpaintingActive, setEraserMode, setBrushSize, setMaskData, clearInpainting, openLocalInpainting, setLocalUploadImage };
}
